import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { api, errMsg, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, UserAvatar } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const F = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

export default function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({
    name: user.name || "", city: user.city || "", bio: user.bio || "", age: user.age || "", gender: user.gender || "",
    instagram: user.instagram || "", tiktok: user.tiktok || "", profile_pic: user.profile_pic || "",
    notif_prefs: user.notif_prefs || { text: true, email: true },
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onPic = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const url = await uploadFile(file); setF((p) => ({ ...p, profile_pic: url })); }
    catch (err) { toast.error(errMsg(err)); }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me", { ...f, age: f.age ? Number(f.age) : null, profile_pic: f.profile_pic || null });
      setUser(data.user || { ...user, ...f });
      toast.success("Profile saved");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Page className="max-w-3xl">
      <p className="font-mono text-xs uppercase tracking-widest text-stone-500">Account</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Your profile</h1>
      <form onSubmit={save} className="mt-10 space-y-6 p-6 sm:p-8 rounded-3xl bg-white border border-stone-200">
        <div className="flex items-center gap-5">
          <UserAvatar user={{ ...user, profile_pic: f.profile_pic, name: f.name }} className="h-24 w-24" testId="profile-avatar" />
          <label data-testid="profile-pic-upload" className="cursor-pointer inline-flex items-center gap-2 border border-stone-300 hover:border-stone-900 rounded-full px-4 py-2 text-sm font-semibold transition-colors">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Change photo
            <input type="file" accept="image/*" className="hidden" onChange={onPic} data-testid="profile-pic-input" />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <F label="Name"><Input data-testid="profile-name-input" value={f.name} onChange={set("name")} required /></F>
          <F label="City"><Input data-testid="profile-city-input" value={f.city} onChange={set("city")} /></F>
          <F label="Age"><Input data-testid="profile-age-input" type="number" value={f.age} onChange={set("age")} /></F>
          <F label="Gender"><Input data-testid="profile-gender-input" value={f.gender} onChange={set("gender")} /></F>
          <F label="Instagram"><Input data-testid="profile-instagram-input" value={f.instagram} onChange={set("instagram")} placeholder="handle" /></F>
          <F label="TikTok"><Input data-testid="profile-tiktok-input" value={f.tiktok} onChange={set("tiktok")} placeholder="handle" /></F>
        </div>
        <F label="Bio"><Textarea data-testid="profile-bio-input" value={f.bio} onChange={set("bio")} rows={3} /></F>
        <div className="space-y-3">
          <Label>Notifications</Label>
          {["email", "text"].map((k) => (
            <div key={k} className="flex items-center justify-between rounded-2xl bg-stone-100 px-4 py-3 text-sm">
              <span className="capitalize">{k} updates</span>
              <Switch data-testid={`profile-notif-${k}`} checked={!!f.notif_prefs[k]} onCheckedChange={(v) => setF({ ...f, notif_prefs: { ...f.notif_prefs, [k]: v } })} />
            </div>
          ))}
        </div>
        <p className="text-xs text-stone-500 font-mono">{user.email}</p>
        <button data-testid="profile-save-btn" disabled={busy || uploading} className="h-12 px-8 rounded-full bg-[#FF5C00] hover:bg-[#E04E00] disabled:opacity-60 text-white font-semibold transition-colors">{busy ? "Saving..." : "Save changes"}</button>
      </form>
      <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <Link to="/privacy" data-testid="profile-privacy-link" className="text-stone-600 hover:text-stone-900">Privacy policy</Link>
        <Link to="/terms" data-testid="profile-terms-link" className="text-stone-600 hover:text-stone-900">Terms of service</Link>
        <Link to="/account/delete" data-testid="profile-delete-account-link" className="text-red-600 hover:text-red-700 font-semibold">Delete account</Link>
      </div>
    </Page>
  );
}
